import {
  UploadFileInfo,
  UploadOnAddEvent,
  UploadOnProgressEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../../common/global/helpers';
import Button from '../../../../../../../common/widgets/Button';
import Checkbox from '../../../../../../../common/widgets/Checkbox';
import FileInput from '../../../../../../../common/widgets/FileInput';
import Icons from '../../../../../../../common/widgets/Icons';
import {
  Notification,
  NotificationType,
} from '../../../../../../../common/widgets/Notification';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { fileImportService } from '../../../../../services/file-import';
import { onRemoveRequest, onSaveRequest } from '../helper';

interface ImportEmployeesFileFormState {
  isDownlodingTemplate: boolean;
  isSaving: boolean;
  removeUsers: boolean;
  files: UploadFileInfo[];
}

export default function ImportEmployeesFileForm() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const defaultState: ImportEmployeesFileFormState = {
    isDownlodingTemplate: false,
    isSaving: false,
    removeUsers: false,
    files: [] as UploadFileInfo[],
  };

  const [state, setState] =
    useState<ImportEmployeesFileFormState>(defaultState);
  const [errors, setErrors] = useState<ErrorState>({});

  const clearErrors = (field: keyof typeof errors) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const onAdd = (event: UploadOnAddEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
    clearErrors('file');
  };

  const onRemove = (event: UploadOnRemoveEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
    clearErrors('file');
  };

  const onProgress = (event: UploadOnProgressEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
    clearErrors('file');
  };

  const handleRemoveUsersChange = () => {
    setState(
      produce(d => {
        d.removeUsers = !d.removeUsers;
      }),
    );
  };

  const handleDownloadEmployeeExample = async () => {
    setState(
      produce(d => {
        d.isDownlodingTemplate = true;
      }),
    );
    const response = await fileImportService.downloadEmployeeTemplate();
    if (response.hasErrors) {
      showErrorToast(
        translate(resources.admin.fileImport.downloadTemplateError),
      );
    }
    setState(
      produce(d => {
        d.isDownlodingTemplate = false;
      }),
    );
  };

  const handleSubmit = async () => {
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    const formData = new FormData();
    if (state.files[0].getRawFile) {
      formData.append('file', state.files[0].getRawFile(), state.files[0].name);
    }

    formData.append('removeUsers', state.removeUsers.toString());
    const response = await fileImportService.saveEmployeesImport(formData);
    if (response.hasErrors) {
      if (response.errorType === ErrorType.ValidationError) {
        setValidationErrors(response, setErrors);
        console.log('errors', errors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      showSuccessToast(
        translate(resources.admin.fileImport.budgetTab.importSuccess),
      );
      setState(defaultState);
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  return (
    <>
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Warning}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: undefined,
          }))}
          showClose={true}
        />
      )}
      <div className="upload-steps">
        <div className="upload-step">
          <h2>{translate(resources.admin.fileImport.stepOneTitle)}</h2>
          <p>
            {translate(
              resources.admin.fileImport.employeeTab.stepOneDescription,
            )}
          </p>
          <div className="upload-actions">
            <Button
              className="btn btn-solid btn-sm"
              onClick={handleDownloadEmployeeExample}
              loading={state.isDownlodingTemplate}
              icon={<Icons.DownloadFile />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.fileImport.stepOneActions.downloadTemplate,
                )}
              </span>
            </Button>
            <Button
              className="btn btn-outline btn-sm"
              onClick={() => navigate('edit-template')}
              icon={<Icons.Cog />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.fileImport.stepOneActions.editTemplate,
                )}
              </span>
            </Button>
          </div>
        </div>

        <div className="upload-step">
          <h2>{translate(resources.admin.fileImport.stepTwoTitle)}</h2>
          <p>
            {translate(
              resources.admin.fileImport.employeeTab.stepTwoDescription,
            )}
          </p>
          <div className="admin-upload">
            <FileInput
              batch={false}
              multiple={false}
              files={state.files}
              onAdd={onAdd}
              onRemove={onRemove}
              onProgress={onProgress}
              onStatusChange={onStatusChange}
              withCredentials={false}
              saveUrl={onSaveRequest}
              removeUrl={onRemoveRequest}
              errors={errors.file}
            />
          </div>
        </div>
        <div className="upload-step">
          <p>
            {translate(resources.admin.fileImport.closeAccountsDescription)}
          </p>
          <Checkbox
            label={translate(resources.admin.fileImport.closeAccountsLabel)}
            isChecked={state.removeUsers}
            onChange={handleRemoveUsersChange}
          ></Checkbox>
        </div>
        <div className="form-actions left-actions">
          <Button
            className="btn btn-solid"
            onClick={handleSubmit}
            loading={state.isSaving}
          >
            <span className="btn-text">
              {translate(resources.admin.action.upload)}
            </span>
          </Button>
          <Button className="btn btn-outline" onClick={() => navigate(-1)}>
            <span className="btn-text">
              {translate(resources.admin.fileImport.actions.close)}
            </span>
          </Button>
        </div>
      </div>
    </>
  );
}
